// "Think our answer is wrong?" on the mock report: starts the two-reviewer
// check, polls for it, and reveals the result — flipping `hidden` on blocks the
// server already rendered, as question_elaboration.js does for "Unpack this
// question", whose shape this is.
//
// Five states live in mock_reports/_key_contest.html.erb and the server
// decides the first; this module only moves between offer, working, failed and
// result. The result's HTML comes back in the poll payload, rendered from
// mock_reports/_key_contest_result by the server — the reviewers' reasoning
// escaped by mock_prose — so the browser inserts it and never assembles one.
//
// Without this module every state still works: the check is a real form that
// posts and redirects back to the question, and the send is a real form.
//
// Gives up on the one clock question_elaboration.js uses: STALE_AFTER shipped
// in the data attribute from the model, plus a grace, checked on EVERY path
// that loops, so no failure mode polls for ever.

const FIRST_POLL_MS = 2000;
const MAX_POLL_MS = 6000;
const GRACE_MS = 30000;

function csrfToken() {
  const meta = document.querySelector("meta[name=csrf-token]");
  return meta ? meta.content : "";
}

function setupCard(root) {
  const url = root.dataset.url;
  const staleAfterMs = Number(root.dataset.staleAfterMs) || 480000;

  const blocks = {
    offer: root.querySelector("[data-key-contest-offer]"),
    working: root.querySelector("[data-key-contest-working]"),
    failed: root.querySelector("[data-key-contest-failed]"),
    result: root.querySelector("[data-key-contest-result]"),
  };
  const error = root.querySelector("[data-key-contest-error]");

  let startedAt = 0;
  let timer = null;

  function show(state) {
    Object.entries(blocks).forEach(([name, el]) => {
      if (el) el.hidden = name !== state;
    });
    root.dataset.state = state;
  }

  // `html` is the server's own render of _key_contest_result from a
  // same-origin JSON reply — the same trust as the page itself.
  function land(html) {
    if (blocks.result) blocks.result.innerHTML = html;
    show("result");
  }

  function apply(data) {
    if (data.status === "ready" && data.html) return land(data.html);
    if (data.restartable) return show("failed");
    if (data.working) return schedule();
    return show("failed");
  }

  function poll() {
    fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => (data === null ? schedule() : apply(data)))
      .catch(schedule);
  }

  function schedule() {
    const elapsed = Date.now() - startedAt;
    if (elapsed > staleAfterMs + GRACE_MS) return show("failed");
    timer = window.setTimeout(poll, Math.min(MAX_POLL_MS, FIRST_POLL_MS + elapsed / 10));
  }

  function refuse(message) {
    if (error) {
      error.textContent = message;
      error.hidden = false;
    }
    show("offer");
  }

  function start(event) {
    event.preventDefault();
    if (error) error.hidden = true;
    window.clearTimeout(timer);
    startedAt = Date.now();
    show("working");

    fetch(url, {
      method: "POST",
      headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
      credentials: "same-origin",
    })
      .then((response) => response.json().then((data) => ({ status: response.status, data })))
      .then(({ status, data }) => {
        if (status === 429) return refuse(data.message || "You have asked for a lot of these today.");
        if (status >= 400) return refuse("We could not start this one. Try again in a moment.");
        return apply(data);
      })
      .catch(() => refuse("We could not reach the server. Check your connection and try again."));
  }

  root.querySelectorAll("[data-key-contest-start-form]").forEach((form) => {
    form.addEventListener("submit", start);
  });

  // Arrived while the reviewers were still working (ours or somebody else's).
  if (root.dataset.state === "working") {
    startedAt = Date.now();
    schedule();
  }
}

function setup() {
  document.querySelectorAll("[data-key-contest]").forEach(setupCard);
}

// The same guard every module in this pack carries — see mock_request_wizard.js.
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", setup);
} else {
  setup();
}
